import { TextSelection } from '@atlaskit/editor-prosemirror/state';

import type { API } from '../aiStreamingOrchestratorPluginType';

/** A resume target, identified by the aborted tool call's id. */
export type ResumeRange = {
	toolCallId: string;
};

/**
 * Resolve the range a resumed (timeout-aborted) tool call should re-apply into,
 * by reading the LATEST (transaction-remapped) stored position for its
 * `toolCallId`. The stored position is cleared as part of resolving it.
 *
 * Reading at apply time (rather than from a snapshot taken when the resume was
 * queued) is what makes resuming MULTIPLE aborted tool calls correct: the bridge
 * fires every resume in one synchronous loop, so any queued snapshot would
 * reflect the document as it was *before any* resume applied. Each earlier resume
 * then mutates the document, and the ai-session-context plugin remaps every
 * still-pending aborted position through `tr.mapping`. Reading the up-to-date
 * value here anchors each resume to its own content (which may span multiple
 * nodes for `appendNodeToEnd` / `replaceNode`) instead of collapsing every
 * resume onto the same stale, shared range.
 *
 * Returns `undefined` when no position is stored for the id (it should always be
 * present — content is streamed before a timeout abort); the caller decides how
 * to degrade in that case.
 */
export const resolveResumePositions = (
	api: API,
	resume: ResumeRange,
): { fromPos: number; toPos: number } | undefined => {
	const live = api.aiExperience.actions.getAbortedToolCallPosition?.(resume.toolCallId);
	// The aborted tool call is being resumed now; drop its stored position so it
	// is not remapped again by subsequent resumes' transactions.
	api.aiExperience.actions.clearAbortedToolCallPosition?.(resume.toolCallId);

	if (live) {
		return { fromPos: live.startPos, toPos: live.aiContentEndPos };
	}
	return undefined;
};

/**
 * Resume-mode selection setter for re-applying a tool call aborted by the queue
 * timeout. Unlike the tool-call actions, it does NOT resolve a node or insert a
 * placeholder (which would re-resolve to end-of-doc); it just sets the selection
 * to the stored, mapped range (`from` / `to`).
 *
 * The resume flow drives `openAIModal` with `isExternal` set, and in that mode it
 * reads its initial range from `state.selection`. Setting the selection here is
 * therefore the reliable way to re-apply where the aborted call left off. (The
 * `overrideFromPos` / `overrideToPos` props are honoured by other, non-external
 * callers but are not used by this path.) Positions are clamped to current doc
 * bounds, as they originate from an earlier (remapped) transaction.
 */
export const createReapplyCommand =
	(api: API) =>
	(from: number, to: number): boolean => {
		return api.core.actions.execute(({ tr }) => {
			const docSize = tr.doc.content.size;
			const safeFrom = Math.max(0, Math.min(from, docSize));
			const safeTo = Math.max(safeFrom, Math.min(to, docSize));
			const selection = TextSelection.create(tr.doc, safeFrom, safeTo);
			return tr.setSelection(selection).scrollIntoView();
		});
	};
